export type ArticleBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'quote'; text: string }
  | { type: 'list'; items: string[] };

export type Article = {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categoryName: string;
  cover: string;
  coverAlt: string;
  author: string;
  date: string;
  readTime: number;
  views: number;
  tags: string[];
  hot: boolean;
  rank: number;
  onstage: string;
  offstage: string;
  content: ArticleBlock[];
};

export const articles: Article[] = [
  {
    id: 1,
    slug: 'live-blackout-three-minutes',
    title: '连麦到一半突然黑屏下播，顶流主播"设备故障"的那三分钟',
    excerpt:
      '前一秒还在笑着接梗，下一秒画面一黑、声音切断。事后官方解释是网络波动，但录屏里那句没被消音的话，成了这场反差最直接的注脚。',
    category: 'live',
    categoryName: '直播翻车现场',
    cover:
      'https://readdy.ai/api/search-image?query=Cinematic%20close%20up%20of%20a%20dark%20empty%20streaming%20studio%20with%20a%20single%20ring%20light%20still%20glowing%20above%20an%20abandoned%20chair%2C%20warm%20amber%20spill%20light%20falling%20on%20a%20microphone%20and%20headphones%2C%20cool%20teal%20shadows%20in%20the%20background%2C%20moody%20editorial%20photography%2C%20film%20grain%2C%20high%20contrast%2C%20no%20people&width=900&height=560&seq=art-live-blackout&orientation=landscape',
    coverAlt: '黑屏后的空直播间，只剩补光灯还亮着',
    author: '反差观察员',
    date: '2026-08-28',
    readTime: 6,
    views: 128400,
    tags: ['直播', '翻车', '录屏取证'],
    hot: true,
    rank: 1,
    onstage: '镜头里全程笑脸接梗，对连麦观众一口一个"家人们"，语气温柔得像在哄人。',
    offstage: '麦克风没关干净的那两秒里，传来一句明显不耐烦的抱怨，随后画面直接切断。',
    content: [
      {
        type: 'p',
        text: '这场直播原本是一次普通的连麦互动。开场十分钟，主播的状态几乎挑不出毛病：语速平稳、笑容挂在脸上，连观众报出的刁钻问题都用玩笑化解。直到一位连麦者反复追问同一个话题，画面出现了那次著名的"三分钟黑屏"。',
      },
      { type: 'h2', text: '录屏里被反复放大的两秒' },
      {
        type: 'p',
        text: '黑屏发生在第 47 分钟。官方账号事后给出的说明是"网络波动导致推流中断"，但流出的录屏显示，在画面彻底切断之前，音频里有一句很轻、但情绪明显的话没有被完全消音。正是这两秒，让"设备故障"这个解释变得站不住脚。',
      },
      {
        type: 'quote',
        text: '台上的温和是有成本的，成本就是台下的情绪必须找个地方释放——问题只在于，那个地方会不会被录下来。',
      },
      { type: 'h2', text: '为什么观众对"两副面孔"如此敏感' },
      {
        type: 'list',
        items: [
          '人设越完美，落差越明显：越是被包装成"永远好脾气"，一次失控就越刺眼。',
          '现场素材不会说谎：口播可以圆，录屏不会。',
          '观众要的不是完美，而是可预期：能接受你有脾气，不能接受你假装没有。',
        ],
      },
      {
        type: 'p',
        text: '值得注意的是，评论区并没有一边倒地指责。有相当一部分观众承认"高强度连麦确实消耗人"，他们真正介意的，是事后那句"设备故障"——把观众当成不知道真相的人。反差本身不是问题，反差被否认，才是问题。',
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '这场案例的价值，不在于证明谁脾气差，而在于提醒所有做内容的人：镜头前的状态是一种工作，镜头后的状态是一种真实，两者之间有距离是正常的。真正决定口碑的，是你如何处理这段距离——承认它，还是假装它不存在。',
      },
    ],
  },
  {
    id: 2,
    slug: 'mic-drop-sales-flip',
    title: '带货现场摔麦走人：前一秒"家人们冲"，后一秒转身离场',
    excerpt:
      '数据不好看的那场直播里，主播从热情讲解到突然沉默，中间只隔了不到十分钟。同一张脸，两种温度，被完整记录在一段两小时的原始回放里。',
    category: 'live',
    categoryName: '直播翻车现场',
    cover:
      'https://readdy.ai/api/search-image?query=Dramatic%20editorial%20photo%20of%20a%20handheld%20microphone%20left%20lying%20on%20a%20dark%20studio%20table%20next%20to%20a%20toppled%20product%20box%2C%20warm%20amber%20rim%20light%20from%20the%20left%20and%20cool%20teal%20fill%20from%20the%20right%2C%20cinematic%20still%20life%2C%20moody%20shadow%2C%20film%20grain%2C%20high%20contrast%2C%20no%20people&width=900&height=560&seq=art-live-micdrop&orientation=landscape',
    coverAlt: '被放下的麦克风与翻倒的商品盒',
    author: '节目前线',
    date: '2026-08-24',
    readTime: 5,
    views: 96400,
    tags: ['带货', '情绪管理', '直播回放'],
    hot: true,
    rank: 3,
    onstage: '讲解产品时语速飞快、情绪饱满，反复强调"这个价我把利润全让给你们"。',
    offstage: '数据不及预期后明显沉默，最后直接放下麦克风离场，留下助播独自圆场。',
    content: [
      {
        type: 'p',
        text: '整场直播的前一个半小时堪称模板：产品卖点拆解清晰，节奏紧凑，情绪始终在线。转折出现在中段——实时在线人数开始下滑，转化数据没有跟上讲解强度。',
      },
      { type: 'h2', text: '情绪断崖是怎么发生的' },
      {
        type: 'p',
        text: '从观众视角看，主播的语气变化非常细微：先是重复卖点的频率变高，然后开始频繁看向提词器之外的方向，最后是那句"你们自己看吧"之后的长时间沉默。直播间里的温度，在几分钟内从沸点降到了冰点。',
      },
      {
        type: 'quote',
        text: '热情如果只在数据漂亮时出现，那它就不是性格，而是一种条件反射。',
      },
      { type: 'h2', text: '回放比剪辑更诚实' },
      {
        type: 'list',
        items: [
          '两小时原始回放几乎无法被"选择性剪辑"，过程完整可查。',
          '助播的临场反应反而是全场最稳定的部分，对比之下更明显。',
          '观众记住的往往不是销售额，而是离场时那个背影。',
        ],
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '卖货是高压场景，情绪波动可以理解。但直播是一镜到底的媒介，它不会替你藏起任何一段。对观众而言，一个愿意在低谷时仍把人当人看的主播，远比一个高潮时喊得最响的主播更值得长期关注。',
      },
    ],
  },
  {
    id: 3,
    slug: 'convenience-store-encounter',
    title: '便利店偶遇人气博主：没有滤镜、没有助理的那十分钟',
    excerpt:
      '一位粉丝在深夜便利店认出了常看的美食博主。没有打光、没有脚本，那段被偷偷记下的对话，和大家在视频里认识的她，是两个人，也是同一个人。',
    category: 'offstage',
    categoryName: '台下偶遇实录',
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20night%20convenience%20store%20interior%20with%20glowing%20shelves%20of%20snacks%2C%20soft%20amber%20fluorescent%20light%20on%20a%20quiet%20empty%20aisle%2C%20cool%20blue%20teal%20light%20spilling%20through%20the%20front%20window%20from%20the%20dark%20street%20outside%2C%20cinematic%20editorial%20photography%2C%20cozy%20moody%20atmosphere%2C%20film%20grain%2C%20no%20people&width=900&height=560&seq=art-store-meet&orientation=landscape',
    coverAlt: '深夜便利店货架透出的暖色灯光',
    author: '夜行记录',
    date: '2026-08-19',
    readTime: 4,
    views: 74200,
    tags: ['偶遇', '线下', '真实状态'],
    hot: true,
    rank: 5,
    onstage: '视频里永远妆容精致、语速轻快，每句话都像是设计好的包袱。',
    offstage: '素颜、穿着拖鞋买关东煮，对店员礼貌道谢，被认出后有点不好意思地笑了。',
    content: [
      {
        type: 'p',
        text: '这位博主在平台上以"高能量美食日常"著称，几乎每条视频都是精致出镜、节奏明快。而这次偶遇发生在一个再普通不过的深夜，便利店里只有两个顾客和一位店员。',
      },
      { type: 'h2', text: '安静的十分钟' },
      {
        type: 'p',
        text: '据当事粉丝描述，对方素颜、头发随意扎起，正在货架前挑关东煮。认出之后，粉丝犹豫要不要打招呼，反而是博主先注意到了有人在看她，主动笑了笑。没有合影要求被拒绝，也没有任何"营业式"的客套。',
      },
      {
        type: 'quote',
        text: '不是每个安静的人都高冷，只是镜头外的她，不需要一直说话。',
      },
      { type: 'h2', text: '反差未必是坏事' },
      {
        type: 'list',
        items: [
          '镜头前的兴奋是工作状态，镜头后的安静是休息状态，两者不冲突。',
          '真正让粉丝好感上升的，往往是一些小动作：对店员说谢谢、替后面的人扶住门。',
          '不是所有反差都指向"塌房"，有些反差只是证明了对方也是普通人。',
        ],
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '我们习惯用"反差"两个字暗示某种背叛，但这个案例提醒我们：反差也可以很温柔。一个在镜头前努力发光的博主，在镜头后想安静吃完一碗关东煮，这件事本身就值得被允许。',
      },
    ],
  },
  {
    id: 4,
    slug: 'airport-luggage-help',
    title: '机场偶遇"高冷人设"博主：他主动帮陌生人抬了三次行李',
    excerpt:
      '以面无表情的冷感外形出名，评论区常年刷"生人勿近"。可那天在行李转盘前，他做的事和这个人设完全不搭——而且是没人拍的情况下。',
    category: 'offstage',
    categoryName: '台下偶遇实录',
    cover:
      'https://readdy.ai/api/search-image?query=Quiet%20early%20morning%20airport%20baggage%20carousel%20hall%20with%20soft%20warm%20morning%20light%20streaming%20through%20tall%20windows%2C%20cool%20teal%20shadows%20on%20the%20polished%20floor%2C%20empty%20conveyor%20belt%2C%20cinematic%20editorial%20photography%2C%20calm%20moody%20atmosphere%2C%20film%20grain%2C%20no%20people&width=900&height=560&seq=art-airport-kind&orientation=landscape',
    coverAlt: '清晨机场行李转盘大厅的空镜',
    author: '候机室观察',
    date: '2026-08-15',
    readTime: 4,
    views: 61800,
    tags: ['机场', '路人视角', '人设反转'],
    hot: false,
    rank: 0,
    onstage: '社媒内容冷感克制，穿搭与表情都走"生人勿近"路线，互动极少。',
    offstage: '在行李转盘前连续帮三位陌生人抬行李，全程没看手机，也没找人拍。',
    content: [
      {
        type: 'p',
        text: '这位博主的人设关键词是"冷"：冷色调的穿搭、几乎不笑的照片、极简的文案。粉丝爱的是这种疏离感，但也常有人调侃"他真的会和人说话吗"。',
      },
      { type: 'h2', text: '被路人看见的那一刻' },
      {
        type: 'p',
        text: '目击者称，当天行李转盘出现故障，几件重行李箱卡在传动带边缘。最先走上前帮忙的是这位博主：他先把箱子拽下来，又顺手帮旁边一位带孩子的乘客把推车扶正。前后三次，没有一句话提到自己是博主。',
      },
      {
        type: 'quote',
        text: '人设是给镜头的，教养是给人的。前者可以设计，后者装不出来。',
      },
      { type: 'h2', text: '为什么这次反差加分' },
      {
        type: 'list',
        items: [
          '没有人拍摄，说明行为出自习惯而非表演。',
          '冷感外形与主动帮忙之间的落差，让"反差"走向了正面。',
          '观众更愿意相信一个"嘴上冷、手上暖"的人。',
        ],
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '人设与实际行为之间保持距离是常态。区别在于，有的反差让人失望，有的反差让人惊喜。判断标准其实很简单：在没有镜头的地方，那个人做了什么。',
      },
    ],
  },
  {
    id: 5,
    slug: 'fanclub-manager-tell',
    title: '后援会前管理自述：我见过镜头外最真实的那一面',
    excerpt:
      '她做过三年粉丝管理，参与过应援、控评、送机。她不是来爆料的，她只是想说说，一个人如何在镜头前和镜头后，慢慢变成两种样子。',
    category: 'fanleak',
    categoryName: '粉丝爆料',
    cover:
      'https://readdy.ai/api/search-image?query=Overhead%20editorial%20still%20life%20of%20a%20laptop%20screen%20glowing%20with%20an%20empty%20group%20chat%20interface%20on%20a%20dark%20wooden%20desk%2C%20warm%20amber%20desk%20lamp%20light%20from%20one%20side%20and%20cool%20teal%20screen%20glow%20from%20the%20other%2C%20scattered%20sticky%20notes%2C%20cinematic%20moody%20photography%2C%20film%20grain%2C%20no%20text%20visible&width=900&height=560&seq=art-fanclub-tell&orientation=landscape',
    coverAlt: '深夜桌面上的聊天界面与便签',
    author: '匿名投稿',
    date: '2026-08-11',
    readTime: 7,
    views: 88900,
    tags: ['后援会', '粉丝生态', '第一视角'],
    hot: true,
    rank: 4,
    onstage: '公开场合发言滴水不漏，采访里永远把粉丝放在第一位。',
    offstage: '私下对团队要求精细到分钟，情绪波动时会把压力转嫁给身边人。',
    content: [
      {
        type: 'p',
        text: '这篇内容来自一位不愿具名的前粉丝管理者。她强调自己不是来"挂人"的，也不提供任何私人信息，她只是想以一个长期站在最近位置的旁观者身份，说说镜头前后的差别是怎么形成的。',
      },
      { type: 'h2', text: '"我们其实都知道" ' },
      {
        type: 'p',
        text: '她的第一句话很平静："距离越近的人，越早看到不一致，只是大家选择了不说。"她解释说，控评、应援、接机这些工作，会让人反复接触到一个人在不同场合的样子，差别不是某一天突然出现的，而是在一次次沟通中慢慢累积。',
      },
      {
        type: 'quote',
        text: '粉丝最难的不是接受偶像有缺点，而是接受自己花的时间，没有被同样认真对待。',
      },
      { type: 'h2', text: '她观察到的三个规律' },
      {
        type: 'list',
        items: [
          '越依赖"完美人设"的内容，崩得越突然，因为人设没有留出空隙。',
          '团队与本人态度常常不一致，很多"耍大牌"其实是中间环节的沟通问题。',
          '真正稳定的人，是那些在私下也保持基本礼貌的人，而不是台上最会说话的人。',
        ],
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '这篇自述的价值，不在于提供任何"实锤"，而在于提供了一个行业内部视角：人设是一种产品，粉丝是一群真实的消费者。当两者之间的期待长期不平衡，反差就会以最戏剧化的方式暴露出来。',
      },
    ],
  },
  {
    id: 6,
    slug: 'deleted-group-chat',
    title: '一条被撤回的群聊记录，让"温柔人设"开始松动',
    excerpt:
      '截图流传的当晚，超话里分成两派：一派认为断章取义，一派翻出了更多旧帖。争议的焦点其实只有一个——我们到底该不该要求一个人永远温柔。',
    category: 'fanleak',
    categoryName: '粉丝爆料',
    cover:
      'https://readdy.ai/api/search-image?query=Close%20up%20editorial%20photo%20of%20a%20phone%20screen%20held%20in%20a%20dark%20room%20showing%20a%20minimal%20chat%20bubble%20interface%20with%20a%20recalled%20message%20marker%2C%20warm%20amber%20light%20on%20one%20side%2C%20cool%20teal%20shadow%20on%20the%20other%2C%20cinematic%20moody%20atmosphere%2C%20shallow%20depth%20of%20field%2C%20film%20grain%2C%20illegible%20text&width=900&height=560&seq=art-groupchat&orientation=landscape',
    coverAlt: '暗光中被撤回的聊天记录界面',
    author: '截图档案',
    date: '2026-08-06',
    readTime: 5,
    views: 70300,
    tags: ['群聊', '人设', '舆论'],
    hot: false,
    rank: 0,
    onstage: '对外形象是"永远轻声细语"，评论区回复粉丝时耐心到近乎周全。',
    offstage: '在小范围内表达过强烈不满，措辞与其公开形象差异明显。',
    content: [
      {
        type: 'p',
        text: '事件的起点是一张截图：一段小范围群聊里，某位以"温柔"著称的内容创作者表达了对工作安排的不满。截图在当晚被迅速转发，随后原帖删除，但讨论已经扩散开来。',
      },
      { type: 'h2', text: '争议的两条线' },
      {
        type: 'p',
        text: '支持者认为，任何人都可以在私域表达情绪，把私下抱怨当作公开表态是过度解读；批评者则翻出更多过往帖文，指出问题不在于抱怨本身，而在于长期的"零负面"包装与真实状态之间的落差。',
      },
      {
        type: 'quote',
        text: '我们并不真的要求一个人永远温柔，我们只是不希望那份温柔，是专门演给我们看的。',
      },
      { type: 'h2', text: '值得追问的三个问题' },
      {
        type: 'list',
        items: [
          '截图来源是否完整、语境是否被保留？',
          '讨论的对象是"行为"还是"人品"？前者可以批评，后者需要克制。',
          '如果换作普通人，同一句话会不会被如此放大？',
        ],
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '这类事件几乎没有标准答案。但有一个相对安全的判断原则：评价一个人设，看的应该是它在压力下是否仍愿意保持基本的尊重，而不是它是否永远不出错。',
      },
    ],
  },
  {
    id: 7,
    slug: 'street-stall-candid',
    title: '路边摊监控拍到的那顿饭：没带妆，也没带剧本',
    excerpt:
      '一段时长不到两分钟的路边摊监控画面，成了本周传播最广的素材。没有台词，没有滤镜，只有一个卸下所有包装的人，认真吃完了一碗面。',
    category: 'candid',
    categoryName: '私下被拍',
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20night%20street%20food%20stall%20with%20glowing%20hanging%20bulbs%20and%20steaming%20noodle%20bowls%2C%20amber%20lamp%20light%20pooling%20on%20a%20small%20plastic%20table%20and%20stool%2C%20cool%20teal%20dark%20street%20blurred%20behind%2C%20cinematic%20editorial%20photography%2C%20nostalgic%20moody%20atmosphere%2C%20film%20grain%2C%20no%20people&width=900&height=560&seq=art-streetfood&orientation=landscape',
    coverAlt: '夜晚路边摊的暖黄灯泡与热气腾腾的面碗',
    author: '街角镜头',
    date: '2026-08-02',
    readTime: 4,
    views: 55200,
    tags: ['监控素材', '素颜', '生活状态'],
    hot: false,
    rank: 0,
    onstage: '内容里永远是精修造型、明亮打光和设计过的生活场景。',
    offstage: '在路边摊排队、自己端碗、吃完把桌子擦干净再离开。',
    content: [
      {
        type: 'p',
        text: '素材来自一家普通路边摊的监控。画面质量并不好，但却意外具备一种"证据感"——它既不能摆拍，也很难被剪辑，这让它比多数公开物料更有说服力。',
      },
      { type: 'h2', text: '画面里的细节' },
      {
        type: 'p',
        text: '据摊主回忆，对方是常客，每次都点同样的东西。画面中可以看到他自己排队、自己端碗，吃完后把纸巾和碗归拢到一处，用纸巾擦了擦桌面才离开。全程没有人上前打扰。',
      },
      {
        type: 'quote',
        text: '一个人最真实的教养，常常出现在他以为没人看的时候。',
      },
      { type: 'h2', text: '为什么这类素材更让人信服' },
      {
        type: 'list',
        items: [
          '来源固定、角度固定，天然排斥"挑选最好的那一帧"。',
          '没有台词，观众只能通过行为判断，减少了被引导的空间。',
          '当素材证明的反差是正面的，好感会来得比任何宣传都快。',
        ],
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '并不是所有"被拍"都应该被消费。但当画面里的行为确实呈现出一种朴素的体面，它至少能说明：镜头之外的那个人，也值得被认真对待。',
      },
    ],
  },
  {
    id: 8,
    slug: 'midnight-back-view',
    title: '深夜街头被拍到的背影："营业"与"生活"之间的一条线',
    excerpt:
      '很多人转发那张背影照时说"原来他也会累"。这张照片之所以打动人，不是因为它揭露了什么，而是因为它没有揭露任何东西——只是一个人下班了。',
    category: 'candid',
    categoryName: '私下被拍',
    cover:
      'https://readdy.ai/api/search-image?query=Rainy%20empty%20city%20street%20at%20midnight%20with%20wet%20glossy%20asphalt%20reflecting%20warm%20amber%20street%20lamps%20and%20cool%20teal%20neon%20signs%2C%20long%20empty%20sidewalk%2C%20cinematic%20editorial%20photography%2C%20lonely%20moody%20atmosphere%2C%20film%20grain%2C%20no%20people&width=900&height=560&seq=art-nightback&orientation=landscape',
    coverAlt: '午夜雨后空荡的街道与霓虹倒影',
    author: '凌晨三点',
    date: '2026-07-29',
    readTime: 4,
    views: 49700,
    tags: ['夜拍', '工作状态', '边界感'],
    hot: false,
    rank: 0,
    onstage: '直播与活动里始终精力充沛，被形容为"像永动机一样"的人。',
    offstage: '结束活动后独自走在凌晨的街头，背影像任何一个刚下班的普通人。',
    content: [
      {
        type: 'p',
        text: '这张照片本身没有任何戏剧性：一个背影、一条街、几盏灯。但正是这种平淡，让它在以"反差"为关键词的讨论里被反复引用。',
      },
      { type: 'h2', text: '反差也可以是"没有反差" ' },
      {
        type: 'p',
        text: '有意思的是，这张图之所以传播，恰恰因为它没有爆料。它用一张最普通的照片，说明了一个常被忽略的事实：所谓台上台下，并不是两个不同的人，而是同一个人在不同时间点的状态。',
      },
      {
        type: 'quote',
        text: '台上是职业，台下是生活，中间的这条线不该被随意越过。',
      },
      { type: 'h2', text: '关于"偷拍"这件事，我们的立场' },
      {
        type: 'list',
        items: [
          '公共场所的常规拍摄，与跟踪、蹲守、翻找隐私是两件完全不同的事。',
          '我们只呈现与"公开言行不一致"相关的信息，不涉及私人住所与联系方式。',
          '未成年人、素人家庭成员不会出现在任何素材中。',
        ],
      },
      { type: 'h2', text: '观察结论' },
      {
        type: 'p',
        text: '把一个人当"机器"是最苛刻的期待。允许他们在镜头之外累、沉默、不营业，本质上是在维护一种基本的边界。这也是本站内容一直强调的：看现场，但守住线。',
      },
    ],
  },
];

export const getArticleBySlug = (slug: string): Article | undefined =>
  articles.find((item) => item.slug === slug);

export const getArticlesByCategory = (category: string): Article[] =>
  articles.filter((item) => item.category === category);

export const hotArticles = articles
  .filter((item) => item.hot)
  .sort((a, b) => a.rank - b.rank);